import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, finalize, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { User } from '../models/shared-component/user';
import { SignInRequest } from '../models/shared-component/sign-in-request.model';
import { SignInResponse } from '../models/shared-component/sign-in-response';
import { PermissionGroupItem, UpdateCurrentRoleModel, UserRoleMapping } from '../models/user-management-model/permission-group';
import { from } from 'linq-to-typescript';
import { Action } from '../models/shared-component/action';
import { UserManagementService } from '../core/services/user-management/user-management.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  currentUserSubject: BehaviorSubject<User | null>;
  currentUser$: Observable<User | null>;

  userRoleMappingSubject: BehaviorSubject<UserRoleMapping[] | null>;
  userRoleMapping$: Observable<UserRoleMapping[] | null>;

  currentRoleSubject: BehaviorSubject<UserRoleMapping | null>;

  private cachedActions: { [key: string]: Action } = {};

  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  get currentUserValue(): User {
    return this.currentUserSubject.value!;
  }

  set currentUserValue(user: User) {
    this.currentUserSubject.next(user);
  }

  constructor(private http: HttpClient,
    private router: Router,
    private userManagementService: UserManagementService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.currentUserSubject = new BehaviorSubject<User | null>(null);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.userRoleMappingSubject = new BehaviorSubject<UserRoleMapping[] | null>(null);
    this.userRoleMapping$ = this.userRoleMappingSubject.asObservable();
  }

  signIn(signInRequest: SignInRequest): Observable<SignInResponse> {
    return this.http.post<SignInResponse>(
      `${environment.api}/auth`,
      signInRequest,
      { headers: this.headers }
    );
  }

  refreshToken(): Observable<SignInResponse> {
    return this.http.post<SignInResponse>(
      `${environment.authApi}/auth/refresh-token`,
      {
        headers: this.headers,
      }
    );
  }

  setAuthFromLocalStorage(auth: SignInResponse): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  getUserByToken(): Observable<User | undefined> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    let userprofile =  this.http.get<User>(`${environment.authApi}/Auth/me`).pipe(
      switchMap((profile: User) => {
        if (profile) {
          // If profile exists, run both requests in parallel
          return forkJoin({
            user: of(profile),
            userRoleMapping: this.userManagementService.getUserRoleMappingByUserId(profile.id)
          });
        } else {
          this.logout();
          return of({ user: undefined, userRoleMapping: [] });  // If no profile, return empty data
        }
      }),
      map(({ user, userRoleMapping }) => {  // Destructure the object correctly
        if (user) {
          this.currentUserSubject = new BehaviorSubject<User | null>(user);
          const roles = userRoleMapping || [];
          this.userRoleMappingSubject = new BehaviorSubject<UserRoleMapping[] | null>(roles);

          // Find current role
          const currentRole = roles.find(role => role.isCurrentRole) || roles[0] || null;
          this.currentRoleSubject = new BehaviorSubject<UserRoleMapping | null>(currentRole || null);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );

    return userprofile;
  }

  private getAuthFromLocalStorage(): SignInResponse | undefined {
    try {
      const authData = JSON.parse(localStorage.getItem(this.authLocalStorageToken)!);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  logout() {
    localStorage.removeItem(this.authLocalStorageToken);
    window.close();
  }

  decryptToken(token: string): Observable<SignInResponse> {
    return this.http
      .post<any>(`${environment.authApi}/Auth/decrypt`, { authToken: token }, { headers: this.headers });
  }

  isAllowedToGrantSystem(mstSystemId: number): boolean {
    if (this.currentRoleSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) => a?.mstSubsystem?.mstSystemId === mstSystemId);
      return isAllowed;
    }
    else {
      return false;
    }
  }

  isAllowedToGrantSubsystem(mstSystemId: number, mstSubsystemId: number): boolean {
    if (this.currentRoleSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstSubsystem?.mstSystemId === mstSystemId);
      return isAllowed;
    }
    else {
      return false;
    }
  }

  /** path ที่เปิดให้ทุก role เข้าได้ ไม่ผูกกับ permission group (ตรงกับ alwaysVisible ใน menu.ts) */
  private readonly publicPaths = ['/help/manual', '/help/video'];

  isPublicPath(path?: string): boolean {
    if (!path) return false;
    return this.publicPaths.some(p => path.startsWith(p));
  }

  isAllowedToGrantSystemByPath(path?: string): boolean {
    if (this.currentRoleSubject.value && path) {
      let isAllowed = from<PermissionGroupItem>(this.currentRoleSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) => a?.mstSubsystem?.slug.includes(path)) || this.isAdmin;
      return isAllowed;
    }
    else {
      return false;
    }
  }

  isAllowedToGrantSubsystemByPath(path?: string): boolean {
    if (this.currentRoleSubject.value && path) {
      let isAllowed = from<PermissionGroupItem>(this.currentRoleSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) => a?.mstSubsystem?.slug.includes(path)) || this.isAdmin;
      return isAllowed;
    }
    else {
      return false;
    }
  }

  isAllowedToGrantSystemByPaths(paths?: string[]): boolean {
    // ถ้าเป็น Admin ให้ผ่านเลย ไม่ต้องเช็ค Path
    if (this.isAdmin) return true;

    if (this.currentRoleSubject.value && paths && paths.length > 0) {
      return from<PermissionGroupItem>(this.currentRoleSubject.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) =>
          // เช็คว่า slug ของ item นั้นๆ มีอยู่ใน list ของ paths ที่ส่งมาหรือไม่
          paths.some(path => a?.mstSubsystem?.slug.includes(path))
        );
    }
    return false;
  }

  isAllowedToGrantSubsystemByPaths(paths?: string[]): boolean {
    // ใช้ logic เดียวกันกับข้างบน
    return this.isAllowedToGrantSystemByPaths(paths);
  }

  get isAdmin(): boolean {
    if (this.currentUserSubject.value) {
      return this.currentUserSubject.value.username == 'testuser_admin' || (this.currentRoleSubject.value?.permissionGroup?.mstGroupAccount?.title.includes("Administrator") ?? false);
    }
    else {
      return false;
    }
  }

  get isAllowedToSeeInActiveInfo(): boolean {
    if (this.currentRoleSubject.value) {
      let isAllowed = from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
        .any((a: PermissionGroupItem) => a?.mstActionId === 5);
      return isAllowed;
    }
    else {
      return false;
    }
  }


  isAllowedToAction(mstSubsystemId: number): Action {
    //console.log("test");
    if (this.currentRoleSubject?.value) {
      return {
        canView:
          from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
            .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 1),

        canCreate:
          from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
            .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 2),

        canDelete:
          from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
            .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 3),

        canUpdate:
          from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
            .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 4),

        canReport:
          from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
            .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 7),

        canApprove: from<PermissionGroupItem>(this.currentRoleSubject?.value?.permissionGroup?.permissionGroupItems)
          .any((a: PermissionGroupItem) => a?.mstSubsystemId === mstSubsystemId && a?.mstActionId === 5)

      } as Action;
    }
    else {
      return new Action();
    }
  }

  isAllowedToActionByLink(link: string): Action {
    const currentRole = this.currentRoleSubject.value;

    if (!currentRole) {
      return new Action();
    }

    // Return cached permission if available
    if (this.cachedActions[link]) {
      return this.cachedActions[link];
    }

    const permissions = currentRole.permissionGroup?.permissionGroupItems || [];

    const action: Action = {
      canView: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 1) || this.isAdmin,
      canCreate: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 2) || this.isAdmin,
      canDelete: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 3) || this.isAdmin,
      canUpdate: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 4) || this.isAdmin,
      canReport: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 7) || this.isAdmin,
      canApprove: permissions.some(a => a?.mstSubsystem?.slug.includes(link) && a?.mstActionId === 5) || this.isAdmin
    };

    // Store result in cache
    this.cachedActions[link] = action;

    return action;
  }

  onSelectCurrentRole(selectRole: UserRoleMapping) {
    let request: UpdateCurrentRoleModel = {
      userId: selectRole.userId,
      permissionGroupId: selectRole.permissionGroupId   
    };

    this.userManagementService.updateUserCurrentRole(request).subscribe(
      res => {
        this.cachedActions = {};

        this.getUserByToken().subscribe(() => {
          const currentUrl = window.location.href;
          //mean new role have acces current url
          if (this.isPublicPath(this.router.url) || this.isAllowedToGrantSubsystemByPath(this.router.url)) {
            console.log("have current url access");
            window.location.reload();
          }
          else {
            const subSystemPaths = [
              '/dashboard/index',
              '/manage-information/work',
              '/manage-information/man-power',
              '/manage-information/agency-info',
              '/equipment/tier-index/2',
              '/equipment/tier-index/3',
              '/equipment/tier-index/4',
              '/equipment/tier-index/5',
              '/equipment/tier-index/6',
              '/equipment/tier-index/7',
              '/equipment/tier-index/8',
              '/equipment/tier-index/9',
              '/equipment/tier-index/10',
              '/equipment/tier-index/11',
              '/request-equipment/request-index',
              '/request-equipment/approve-index',
              '/setting/mission',
              '/setting/building-main-type',
              '/setting/building-sub-type',
              '/master-equipment/parcel-index',
              '/master-equipment/meeting-index',
              '/master-equipment/special-branch-index',
              '/master-equipment/immigration-index',
              '/master-equipment/evidence-center-index',
              '/master-equipment/hospital-index',
              '/master-equipment/border-patrol-index',
              '/master-equipment/vehical-index',
              '/master-equipment/armament-index',
              '/master-equipment/tactical-index',
              '/user-management/permission-group',
              '/user-management/user',
            ];

            const firstAccessiblePath = subSystemPaths.find(path =>
              this.isAllowedToGrantSubsystemByPath(path)
            );

            if (firstAccessiblePath) {
              window.location.replace(firstAccessiblePath);
            } else {
              window.location.replace('/error?title=No Any Permission.');
            }
          }
        });
        //window.location.reload();
      }
    )
  }
}
